import { useEffect, useState } from "react";
import Layout from "../../components/user/Layout";
import Helmet from "react-helmet";
import { useSelector } from "react-redux";
import { FiTrendingUp } from "react-icons/fi";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import apiRequest from "../../utils/apiRequest";
import toast from "react-hot-toast";
import { formatDate } from "../../utils/helper";

const Dashboard = () => {
  const siteName = useSelector(
    (state) => state.globalState?.siteSetting?.site_settings?.site_name
  );
  const [dashboard, setDashbaordData] = useState(null);
  const [loading, setLoading] = useState(true);

  // useEffect(() => {
  //   apiRequest
  //     .getPartnerDashboardData()
  //     .then((data) => {
  //       setDashbaordData(data?.data?.data);
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //       toast.error("Unable to load data.");
  //       setLoading(false);
  //     });
  // }, []);

  const COLORS = ["#10B981", "#34D399", "#3B82F6", "#6366F1", "#A78BFA"];
  const statusColors = {
    Pending: "bg-yellow-100 text-yellow-800",
    Dispatched: "bg-green-100 text-green-800",
    Cancelled: "bg-red-100 text-red-800",
    Delivered: "bg-blue-100 text-blue-800",
  };


  return (
    <Layout>
      <Helmet>
        <title>Dashboard - {siteName}</title>
      </Helmet>

      <div className="p-6">
        <h1 className="mb-6 text-3xl font-bold text-gray-800">
          Partner Dashboard
        </h1>

        {/* Metric Cards */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <ShimmerCard key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              title="Total Products"
              value={dashboard?.stacks?.totalProducts}
            />
            <MetricCard
              title="Orders Received"
              value={dashboard?.stacks?.orderReceived}
            />
            <MetricCard
              title="Orders Dispatched"
              value={dashboard?.stacks?.orderDispatched}
            />
            <MetricCard
              title="Orders Cancelled"
              value={dashboard?.stacks?.orderCancelled}
            />
            <MetricCard
              title="Commission Generated"
              value={`₹ ${dashboard?.stacks?.commissionGenerated}`}
            />
            <MetricCard
              title="Commission Paid"
              value={`₹ ${dashboard?.stacks?.totalPaid}`}
            />
            <MetricCard
              title="Commission Left"
              value={`₹ ${dashboard?.stacks?.totalLeft}`}
            />
          </div>
        )}

        {/* Bar and Line Charts */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2">
            <ShimmerChart />
            <ShimmerChart />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2">
            <ChartCard title="Monthly Commission & Orders">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboard?.salesData} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="commission"
                    name="Commission (₹)"
                    fill="#10B981"
                    radius={[10, 10, 0, 0]}
                  />
                  <Bar
                    dataKey="orders"
                    name="Orders"
                    fill="#3B82F6"
                    radius={[10, 10, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Commission Trend">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboard?.salesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="commission"
                    stroke="#10B981"
                    strokeWidth={3}
                    dot={{ r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Commission (₹)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        {/* Pie + Recent Orders */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-5">
            <ShimmerChart className="lg:col-span-3 h-[300px]" />
            <ShimmerChart className="lg:col-span-2 h-[300px]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-5">
            {/* Recent Orders Table */}
            <ChartCard
              title="Recent Orders"
              className="overflow-x-auto lg:col-span-3"
            >
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboard?.recentOrders?.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {order.customer}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        ₹ {order.amount}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 inline-flex text-xs font-semibold rounded ${
                            statusColors[order.status] ||
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDate(order.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ChartCard>

            {/* Pie Chart */}
            <ChartCard
              title="Products by Category"
              className="overflow-x-auto lg:col-span-2"
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboard?.categoryData}
                    dataKey="products"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    labelLine={false}
                  >
                    {dashboard?.categoryData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon }) => (
  <div className="flex items-center gap-4 p-5 transition-shadow bg-white shadow-md rounded-2xl hover:shadow-lg">
    <div className="flex items-center justify-center w-12 h-12 text-xl text-green-600 bg-green-100 rounded-full">
      {icon || <FiTrendingUp />}
    </div>
    <div>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

// Chart Card Wrapper
const ChartCard = ({ title, children, className = "" }) => (
  <div className={`bg-white p-6 rounded-xl ${className}`}>
    <h2 className="mb-4 text-xl font-semibold text-gray-700">{title}</h2>
    {children}
  </div>
);

// Loading shimmer for metrics
const ShimmerCard = ({ className = "" }) => (
  <div className={`bg-white rounded-xl p-5 animate-pulse ${className}`}>
    <div className="w-1/3 h-4 mb-2 bg-gray-200 rounded"></div>
    <div className="w-2/3 h-6 bg-gray-300 rounded"></div>
  </div>
);

// Loading shimmer for charts
const ShimmerChart = ({ className = "" }) => (
  <div className={`bg-white p-6 rounded-xl animate-pulse ${className}`}>
    <div className="w-1/2 h-6 mb-4 bg-gray-200 rounded"></div>
    <div className="h-[240px] bg-gray-100 rounded"></div>
  </div>
);

export default Dashboard;
